import { useState, useEffect, useCallback } from 'react'
import * as Yup from 'yup'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  FileSearch,
  Scissors,
  Filter,
  Target,
  Layers,
  Info,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { DocumentRAGSettings } from '@/redux/types/configuration'
import {
  documentRAGSettingsSchema,
  roomDocumentRAGSettingsSchema,
} from '@/pages/AdvisorSettings/validation'

interface DocumentRAGSettingsProps {
  documentId: number
  settings: DocumentRAGSettings | undefined
  advisorDefaults: {
    ragEnabled: boolean
    ragTopK: number
    ragChunkSize: number
    ragChunkOverlap: number
    ragSimilarityThreshold: number
    ragMaxContextLength: number
  }
  onSettingsChange: (documentId: number, settings: DocumentRAGSettings) => void
  disabled?: boolean
  roomScoped?: boolean
}

export default function DocumentRAGSettingsComponent({
  documentId,
  settings,
  advisorDefaults,
  onSettingsChange,
  disabled = false,
  roomScoped = false,
}: DocumentRAGSettingsProps) {
  const validationSchema = roomScoped
    ? roomDocumentRAGSettingsSchema
    : documentRAGSettingsSchema
  const [useFullContext, setUseFullContext] = useState(
    settings?.use_full_context || false
  )
  const [ragEnabled, setRagEnabled] = useState<boolean | null>(
    settings?.rag_enabled ?? null
  )
  const [topK, setTopK] = useState<number | null>(
    settings?.rag_top_k ?? null
  )
  const [chunkSize, setChunkSize] = useState<number | null>(
    settings?.rag_chunk_size ?? null
  )
  const [chunkOverlap, setChunkOverlap] = useState<number | null>(
    settings?.rag_chunk_overlap ?? null
  )
  const [similarityThreshold, setSimilarityThreshold] = useState<number | null>(
    settings?.rag_similarity_threshold ?? null
  )
  const [maxContextLength, setMaxContextLength] = useState<number | null>(
    settings?.rag_max_context_length ?? null
  )

  // Validation error states
  const [topKError, setTopKError] = useState<string | null>(null)
  const [chunkSizeError, setChunkSizeError] = useState<string | null>(null)
  const [chunkOverlapError, setChunkOverlapError] = useState<string | null>(null)
  const [similarityThresholdError, setSimilarityThresholdError] = useState<string | null>(null)
  const [maxContextLengthError, setMaxContextLengthError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const getValidationErrors = useCallback((settings: DocumentRAGSettings): Record<string, string> => {
    try {
      validationSchema.validateSync(settings, { abortEarly: false })
      return {}
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const out: Record<string, string> = {}
        for (const e of err.inner) {
          const path = e.path ?? '_form'
          if (!out[path]) out[path] = e.message
        }
        return out
      }
      return {}
    }
  }, [validationSchema])

  const applyValidationErrors = useCallback((errors: Record<string, string>) => {
    setTopKError(errors.rag_top_k ?? null)
    setChunkSizeError(errors.rag_chunk_size ?? null)
    setChunkOverlapError(errors.rag_chunk_overlap ?? null)
    setSimilarityThresholdError(errors.rag_similarity_threshold ?? null)
    setMaxContextLengthError(errors.rag_max_context_length ?? null)
    setFormError(errors._form ?? null)
  }, [])

  const buildCurrentSettings = useCallback((): DocumentRAGSettings => {
    const base: DocumentRAGSettings = {
      use_full_context: useFullContext,
      rag_enabled: ragEnabled,
      rag_chunk_size: chunkSize,
      rag_chunk_overlap: chunkOverlap,
    }
    if (roomScoped) {
      return base
    }
    return {
      ...base,
      rag_top_k: topK,
      rag_similarity_threshold: similarityThreshold,
      rag_max_context_length: maxContextLength,
    }
  }, [
    useFullContext,
    ragEnabled,
    topK,
    chunkSize,
    chunkOverlap,
    similarityThreshold,
    maxContextLength,
    roomScoped,
  ])

  useEffect(() => {
    setUseFullContext(settings?.use_full_context || false)
    setRagEnabled(settings?.rag_enabled ?? null)
    setTopK(settings?.rag_top_k ?? null)
    setChunkSize(settings?.rag_chunk_size ?? null)
    setChunkOverlap(settings?.rag_chunk_overlap ?? null)
    setSimilarityThreshold(settings?.rag_similarity_threshold ?? null)
    setMaxContextLength(settings?.rag_max_context_length ?? null)
    // Clear errors when settings change externally
    setTopKError(null)
    setChunkSizeError(null)
    setChunkOverlapError(null)
    setSimilarityThresholdError(null)
    setMaxContextLengthError(null)
    setFormError(null)
  }, [settings])

  const getEffectiveValue = (value: number | null, defaultVal: number) =>
    value !== null ? value : defaultVal

  const handleFullContextChange = useCallback(
    (checked: boolean) => {
      setUseFullContext(checked)
      const newSettings: DocumentRAGSettings = roomScoped
        ? {
            use_full_context: checked,
            rag_enabled: checked ? null : ragEnabled,
            rag_chunk_size: checked ? null : chunkSize,
            rag_chunk_overlap: checked ? null : chunkOverlap,
          }
        : {
            use_full_context: checked,
            rag_enabled: checked ? null : ragEnabled,
            rag_top_k: checked ? null : topK,
            rag_chunk_size: checked ? null : chunkSize,
            rag_chunk_overlap: checked ? null : chunkOverlap,
            rag_similarity_threshold: checked ? null : similarityThreshold,
            rag_max_context_length: checked ? null : maxContextLength,
          }
      const errors = getValidationErrors(newSettings)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onSettingsChange(documentId, newSettings)
      }
    },
    [
      documentId,
      roomScoped,
      ragEnabled,
      topK,
      chunkSize,
      chunkOverlap,
      similarityThreshold,
      maxContextLength,
      onSettingsChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleRagEnabledChange = useCallback(
    (checked: boolean) => {
      setRagEnabled(checked)
      const newSettings: DocumentRAGSettings = roomScoped
        ? {
            use_full_context: useFullContext,
            rag_enabled: checked,
            rag_chunk_size: chunkSize,
            rag_chunk_overlap: chunkOverlap,
          }
        : {
            use_full_context: useFullContext,
            rag_enabled: checked,
            rag_top_k: topK,
            rag_chunk_size: chunkSize,
            rag_chunk_overlap: chunkOverlap,
            rag_similarity_threshold: similarityThreshold,
            rag_max_context_length: maxContextLength,
          }
      const errors = getValidationErrors(newSettings)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onSettingsChange(documentId, newSettings)
      }
    },
    [
      documentId,
      roomScoped,
      useFullContext,
      topK,
      chunkSize,
      chunkOverlap,
      similarityThreshold,
      maxContextLength,
      onSettingsChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleTopKChange = useCallback(
    (value: number[]) => {
      const newValue = value[0]
      setTopK(newValue)
      const newSettings: DocumentRAGSettings = {
        use_full_context: useFullContext,
        rag_enabled: ragEnabled,
        rag_top_k: newValue,
        rag_chunk_size: chunkSize,
        rag_chunk_overlap: chunkOverlap,
        rag_similarity_threshold: similarityThreshold,
        rag_max_context_length: maxContextLength,
      }
      const errors = getValidationErrors(newSettings)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onSettingsChange(documentId, newSettings)
      }
    },
    [
      documentId,
      useFullContext,
      ragEnabled,
      chunkSize,
      chunkOverlap,
      similarityThreshold,
      maxContextLength,
      onSettingsChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleChunkSizeChange = useCallback(
    (value: string) => {
      let numValue: number | null = null
      if (value !== '') {
        const parsed = parseInt(value)
        if (isNaN(parsed)) {
          setChunkSizeError('Chunk size must be a number')
          setChunkSize(null)
          return
        }
        numValue = parsed
      }
      setChunkSize(numValue)
      const newSettings: DocumentRAGSettings = roomScoped
        ? {
            use_full_context: useFullContext,
            rag_enabled: ragEnabled,
            rag_chunk_size: numValue,
            rag_chunk_overlap: chunkOverlap,
          }
        : {
            use_full_context: useFullContext,
            rag_enabled: ragEnabled,
            rag_top_k: topK,
            rag_chunk_size: numValue,
            rag_chunk_overlap: chunkOverlap,
            rag_similarity_threshold: similarityThreshold,
            rag_max_context_length: maxContextLength,
          }
      const errors = getValidationErrors(newSettings)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onSettingsChange(documentId, newSettings)
      }
    },
    [
      documentId,
      roomScoped,
      useFullContext,
      ragEnabled,
      topK,
      chunkOverlap,
      similarityThreshold,
      maxContextLength,
      onSettingsChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleChunkOverlapChange = useCallback(
    (value: string) => {
      let numValue: number | null = null
      if (value !== '') {
        const parsed = parseInt(value)
        if (isNaN(parsed)) {
          setChunkOverlapError('Chunk overlap must be a number')
          setChunkOverlap(null)
          return
        }
        numValue = parsed
      }
      setChunkOverlap(numValue)
      const newSettings: DocumentRAGSettings = roomScoped
        ? {
            use_full_context: useFullContext,
            rag_enabled: ragEnabled,
            rag_chunk_size: chunkSize,
            rag_chunk_overlap: numValue,
          }
        : {
            use_full_context: useFullContext,
            rag_enabled: ragEnabled,
            rag_top_k: topK,
            rag_chunk_size: chunkSize,
            rag_chunk_overlap: numValue,
            rag_similarity_threshold: similarityThreshold,
            rag_max_context_length: maxContextLength,
          }
      const errors = getValidationErrors(newSettings)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onSettingsChange(documentId, newSettings)
      }
    },
    [
      documentId,
      roomScoped,
      useFullContext,
      ragEnabled,
      topK,
      chunkSize,
      similarityThreshold,
      maxContextLength,
      onSettingsChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleSimilarityThresholdChange = useCallback(
    (value: number[]) => {
      const newValue = value[0]
      setSimilarityThreshold(newValue)
      const newSettings: DocumentRAGSettings = {
        use_full_context: useFullContext,
        rag_enabled: ragEnabled,
        rag_top_k: topK,
        rag_chunk_size: chunkSize,
        rag_chunk_overlap: chunkOverlap,
        rag_similarity_threshold: newValue,
        rag_max_context_length: maxContextLength,
      }
      const errors = getValidationErrors(newSettings)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onSettingsChange(documentId, newSettings)
      }
    },
    [
      documentId,
      useFullContext,
      ragEnabled,
      topK,
      chunkSize,
      chunkOverlap,
      maxContextLength,
      onSettingsChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleMaxContextLengthChange = useCallback(
    (value: string) => {
      let numValue: number | null = null
      if (value !== '') {
        const parsed = parseInt(value)
        if (isNaN(parsed)) {
          setMaxContextLengthError('Max context length must be a number')
          setMaxContextLength(null)
          return
        }
        numValue = parsed
      }
      setMaxContextLength(numValue)
      const newSettings: DocumentRAGSettings = {
        use_full_context: useFullContext,
        rag_enabled: ragEnabled,
        rag_top_k: topK,
        rag_chunk_size: chunkSize,
        rag_chunk_overlap: chunkOverlap,
        rag_similarity_threshold: similarityThreshold,
        rag_max_context_length: numValue,
      }
      const errors = getValidationErrors(newSettings)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onSettingsChange(documentId, newSettings)
      }
    },
    [
      documentId,
      useFullContext,
      ragEnabled,
      topK,
      chunkSize,
      chunkOverlap,
      similarityThreshold,
      onSettingsChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  const handleInheritToggle = useCallback(
    (field: keyof DocumentRAGSettings) => {
      const newSettings: DocumentRAGSettings = {
        use_full_context: useFullContext,
        rag_enabled: ragEnabled,
        rag_top_k: topK,
        rag_chunk_size: chunkSize,
        rag_chunk_overlap: chunkOverlap,
        rag_similarity_threshold: similarityThreshold,
        rag_max_context_length: maxContextLength,
      }
      newSettings[field] = null as any
      const errors = getValidationErrors(newSettings)
      applyValidationErrors(errors)
      if (Object.keys(errors).length === 0) {
        onSettingsChange(documentId, newSettings)
      }
      switch (field) {
        case 'rag_enabled':
          setRagEnabled(null)
          break
        case 'rag_top_k':
          setTopK(null)
          break
        case 'rag_chunk_size':
          setChunkSize(null)
          break
        case 'rag_chunk_overlap':
          setChunkOverlap(null)
          break
        case 'rag_similarity_threshold':
          setSimilarityThreshold(null)
          break
        case 'rag_max_context_length':
          setMaxContextLength(null)
          break
      }
    },
    [
      documentId,
      useFullContext,
      ragEnabled,
      topK,
      chunkSize,
      chunkOverlap,
      similarityThreshold,
      maxContextLength,
      onSettingsChange,
      getValidationErrors,
      applyValidationErrors,
    ]
  )

  return (
    <TooltipProvider>
      <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
        {/* Use Full Context Toggle */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <Info className="w-4 h-4 text-blue-600" />
              <Label htmlFor={`full-context-${documentId}`} className="text-sm font-medium">
                Use Full Context
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <QuestionMarkCircleIcon className="w-4 h-4 cursor-help text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    When enabled, this document bypasses RAG and is included in full
                    in the system prompt. All RAG settings will be disabled.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Switch
              id={`full-context-${documentId}`}
              checked={useFullContext}
              onCheckedChange={handleFullContextChange}
              disabled={disabled}
            />
          </div>
          {formError && (
            <p className="text-sm text-red-500 mt-1">{formError}</p>
          )}
        </div>

        {/* RAG Settings (only shown when not using full context) */}
        {!useFullContext && (
          <div className="space-y-4 pt-2 border-t">
            {/* RAG Enabled */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                  <Label htmlFor={`rag-enabled-${documentId}`} className="text-sm font-medium">
                    Enable RAG
                  </Label>
                  {!roomScoped && ragEnabled === null && (
                    <Badge variant="outline" className="text-xs">
                      Inherit ({advisorDefaults.ragEnabled ? 'On' : 'Off'})
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {!roomScoped && ragEnabled !== null && (
                    <button
                      type="button"
                      onClick={() => {
                        setRagEnabled(null)
                        handleInheritToggle('rag_enabled')
                      }}
                      className="text-xs text-blue-600 hover:underline"
                      disabled={disabled}
                    >
                      Inherit
                    </button>
                  )}
                  <Switch
                    id={`rag-enabled-${documentId}`}
                    checked={ragEnabled !== null ? ragEnabled : advisorDefaults.ragEnabled}
                    onCheckedChange={handleRagEnabledChange}
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>

            {/* Top K — advisor only (retrieval uses advisor RAG for room docs) */}
            {!roomScoped && (
            <div className={cn('space-y-2', ragEnabled === false && 'opacity-50')}>
              <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                  <FileSearch className="w-4 h-4 text-green-600" />
                  <Label htmlFor={`top-k-${documentId}`} className="text-sm font-medium">
                    Top K Results
                  </Label>
                  {topK === null && (
                    <Badge variant="outline" className="text-xs">
                      Inherit ({advisorDefaults.ragTopK})
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {topK !== null && (
                    <button
                      type="button"
                      onClick={() => {
                        setTopK(null)
                        handleInheritToggle('rag_top_k')
                      }}
                      className="text-xs text-blue-600 hover:underline"
                      disabled={disabled}
                    >
                      Inherit
                    </button>
                  )}
                  <span className="px-2 py-1 font-mono text-sm rounded bg-muted">
                    {getEffectiveValue(topK, advisorDefaults.ragTopK)}
                  </span>
                </div>
              </div>
              <div className="px-2">
                <Slider
                  id={`top-k-${documentId}`}
                  min={1}
                  max={20}
                  step={1}
                  value={[getEffectiveValue(topK, advisorDefaults.ragTopK)]}
                  onValueChange={handleTopKChange}
                  className="w-full"
                  disabled={disabled || ragEnabled === false}
                />
              </div>
              {topKError && (
                <p className="text-sm text-red-500 mt-1">{topKError}</p>
              )}
            </div>
            )}

            {/* Chunk Size */}
            <div className={cn('space-y-2', ragEnabled === false && 'opacity-50')}>
              <div className="flex gap-2 items-center">
                <Scissors className="w-4 h-4 text-purple-600" />
                <Label htmlFor={`chunk-size-${documentId}`} className="text-sm font-medium">
                  Chunk Size (characters)
                </Label>
                {!roomScoped && chunkSize === null && (
                  <Badge variant="outline" className="text-xs">
                    Inherit ({advisorDefaults.ragChunkSize})
                  </Badge>
                )}
                {!roomScoped && chunkSize !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setChunkSize(null)
                      handleInheritToggle('rag_chunk_size')
                    }}
                    className="text-xs text-blue-600 hover:underline ml-auto"
                    disabled={disabled}
                  >
                    Inherit
                  </button>
                )}
              </div>
              <Input
                id={`chunk-size-${documentId}`}
                type="number"
                min="100"
                max="4000"
                value={chunkSize === null ? '' : chunkSize}
                onChange={(e) => handleChunkSizeChange(e.target.value)}
                onBlur={(e) => {
                  if (e.target.value !== '') {
                    const parsed = parseInt(e.target.value)
                    if (isNaN(parsed)) {
                      setChunkSizeError('Chunk size must be a number')
                      return
                    }
                  }
                  applyValidationErrors(getValidationErrors(buildCurrentSettings()))
                }}
                className={cn(
                  'w-32',
                  chunkSizeError && 'border-red-500 focus:border-red-500 focus:ring-red-500'
                )}
                disabled={disabled || ragEnabled === false}
                placeholder={chunkSize === null ? advisorDefaults.ragChunkSize.toString() : undefined}
              />
              {chunkSizeError && (
                <p className="text-sm text-red-500 mt-1">{chunkSizeError}</p>
              )}
            </div>

            {/* Chunk Overlap */}
            <div className={cn('space-y-2', ragEnabled === false && 'opacity-50')}>
              <div className="flex gap-2 items-center">
                <Layers className="w-4 h-4 text-orange-600" />
                <Label htmlFor={`chunk-overlap-${documentId}`} className="text-sm font-medium">
                  Chunk Overlap (characters)
                </Label>
                {!roomScoped && chunkOverlap === null && (
                  <Badge variant="outline" className="text-xs">
                    Inherit ({advisorDefaults.ragChunkOverlap})
                  </Badge>
                )}
                {!roomScoped && chunkOverlap !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setChunkOverlap(null)
                      handleInheritToggle('rag_chunk_overlap')
                    }}
                    className="text-xs text-blue-600 hover:underline ml-auto"
                    disabled={disabled}
                  >
                    Inherit
                  </button>
                )}
              </div>
              <Input
                id={`chunk-overlap-${documentId}`}
                type="number"
                min="0"
                max="1000"
                value={chunkOverlap === null ? '' : chunkOverlap}
                onChange={(e) => handleChunkOverlapChange(e.target.value)}
                onBlur={(e) => {
                  if (e.target.value !== '') {
                    const parsed = parseInt(e.target.value)
                    if (isNaN(parsed)) {
                      setChunkOverlapError('Chunk overlap must be a number')
                      return
                    }
                  }
                  applyValidationErrors(getValidationErrors(buildCurrentSettings()))
                }}
                className={cn(
                  'w-32',
                  chunkOverlapError && 'border-red-500 focus:border-red-500 focus:ring-red-500'
                )}
                disabled={disabled || ragEnabled === false}
                placeholder={chunkOverlap === null ? advisorDefaults.ragChunkOverlap.toString() : undefined}
              />
              {chunkOverlapError && (
                <p className="text-sm text-red-500 mt-1">{chunkOverlapError}</p>
              )}
            </div>

            {/* Similarity Threshold — advisor only */}
            {!roomScoped && (
            <div className={cn('space-y-2', ragEnabled === false && 'opacity-50')}>
              <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                  <Filter className="w-4 h-4 text-cyan-600" />
                  <Label htmlFor={`similarity-threshold-${documentId}`} className="text-sm font-medium">
                    Similarity Threshold
                  </Label>
                  {similarityThreshold === null && (
                    <Badge variant="outline" className="text-xs">
                      Inherit ({advisorDefaults.ragSimilarityThreshold.toFixed(2)})
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {similarityThreshold !== null && (
                    <button
                      type="button"
                      onClick={() => {
                        setSimilarityThreshold(null)
                        handleInheritToggle('rag_similarity_threshold')
                      }}
                      className="text-xs text-blue-600 hover:underline"
                      disabled={disabled}
                    >
                      Inherit
                    </button>
                  )}
                  <span className="px-2 py-1 font-mono text-sm rounded bg-muted">
                    {getEffectiveValue(similarityThreshold, advisorDefaults.ragSimilarityThreshold).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="px-2">
                <Slider
                  id={`similarity-threshold-${documentId}`}
                  min={0}
                  max={1}
                  step={0.05}
                  value={[getEffectiveValue(similarityThreshold, advisorDefaults.ragSimilarityThreshold)]}
                  onValueChange={handleSimilarityThresholdChange}
                  className="w-full"
                  disabled={disabled || ragEnabled === false}
                />
              </div>
              {similarityThresholdError && (
                <p className="text-sm text-red-500 mt-1">{similarityThresholdError}</p>
              )}
            </div>
            )}

            {/* Max Context Length — advisor only */}
            {!roomScoped && (
            <div className={cn('space-y-2', ragEnabled === false && 'opacity-50')}>
              <div className="flex gap-2 items-center">
                <Target className="w-4 h-4 text-red-600" />
                <Label htmlFor={`max-context-length-${documentId}`} className="text-sm font-medium">
                  Max Context Length (tokens)
                </Label>
                {maxContextLength === null && (
                  <Badge variant="outline" className="text-xs">
                    Inherit ({advisorDefaults.ragMaxContextLength})
                  </Badge>
                )}
                {maxContextLength !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setMaxContextLength(null)
                      handleInheritToggle('rag_max_context_length')
                    }}
                    className="text-xs text-blue-600 hover:underline ml-auto"
                    disabled={disabled}
                  >
                    Inherit
                  </button>
                )}
              </div>
              <Input
                id={`max-context-length-${documentId}`}
                type="number"
                min="500"
                max="8000"
                value={maxContextLength === null ? '' : maxContextLength}
                onChange={(e) => handleMaxContextLengthChange(e.target.value)}
                onBlur={(e) => {
                  if (e.target.value !== '') {
                    const parsed = parseInt(e.target.value)
                    if (isNaN(parsed)) {
                      setMaxContextLengthError('Max context length must be a number')
                      return
                    }
                  }
                  applyValidationErrors(getValidationErrors(buildCurrentSettings()))
                }}
                className={cn(
                  'w-32',
                  maxContextLengthError && 'border-red-500 focus:border-red-500 focus:ring-red-500'
                )}
                disabled={disabled || ragEnabled === false}
                placeholder={maxContextLength === null ? advisorDefaults.ragMaxContextLength.toString() : undefined}
              />
              {maxContextLengthError && (
                <p className="text-sm text-red-500 mt-1">{maxContextLengthError}</p>
              )}
            </div>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
